// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { COMPUTE_RESOURCE_STATUS } from 'admin/computeResource/constants';
import {
    Translate,
    Link,
} from '@plesk/ui-library';
import {
    IconContainer,
    StatusContainer,
    StatusLabelContainer,
} from 'admin/computeResource/common/Status/Styles';
import { ReactComponent as RoundedArrowRight } from 'common/icons/rounded-arrow-right.svg';
import { ReactComponent as RoundedX } from 'common/icons/rounded-x.svg';
import { ReactComponent as RoundedQuestion } from 'common/icons/rounded-question.svg';
import { ReactComponent as RoundedExclamationFilled } from 'common/icons/rounded-exclamation-filled.svg';
import { ReactComponent as RoundedArrowDown } from 'common/icons/rounded-arrow-down.svg';
import { COLORS } from 'common/theme';
import Spinner from 'common/components/Spinner/Spinner';
import { COMPUTE_RESOURCE } from 'admin/computeResource/constants/tests';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { useIsDown } from 'common/hooks/useBreakpoint';
import { BREAKPOINTS } from 'common/styles/breakpoints';


export interface IStatusProps {
    status: COMPUTE_RESOURCE_STATUS;
    onDetailsClick?: () => void;
    withoutButton?: boolean;
}

export const statusTranslationMap: Record<COMPUTE_RESOURCE_STATUS, React.ReactNode> = {
    [COMPUTE_RESOURCE_STATUS.FAILED]: <Translate content="computeResource.statuses.failed" />,
    [COMPUTE_RESOURCE_STATUS.COMMISSIONING]: <Translate content="computeResource.statuses.commissioning" />,
    [COMPUTE_RESOURCE_STATUS.CONFIGURE_NETWORK]: <Translate content="computeResource.statuses.configureNetwork" />,
    [COMPUTE_RESOURCE_STATUS.CONFIGURING_NETWORK]: <Translate content="computeResource.statuses.configuringNetwork" />,
    [COMPUTE_RESOURCE_STATUS.UNAVAILABLE]: <Translate content="computeResource.statuses.unavailable" />,
    [COMPUTE_RESOURCE_STATUS.ACTIVE]: <Translate content="computeResource.statuses.active" />,
};

export const statusesWithDetails = [
    COMPUTE_RESOURCE_STATUS.COMMISSIONING,
    COMPUTE_RESOURCE_STATUS.FAILED,
    COMPUTE_RESOURCE_STATUS.CONFIGURE_NETWORK,
];

const STATUS_ICONS: Record<COMPUTE_RESOURCE_STATUS, React.FC> = {
    [COMPUTE_RESOURCE_STATUS.CONFIGURING_NETWORK]: () => <Spinner />,
    [COMPUTE_RESOURCE_STATUS.ACTIVE]: () => <RoundedArrowRight color={COLORS.SUCCESS} />,
    [COMPUTE_RESOURCE_STATUS.FAILED]: () => <RoundedX color={COLORS.DANGER} />,
    [COMPUTE_RESOURCE_STATUS.UNAVAILABLE]: () => <RoundedQuestion color={COLORS.GREY_70} />,
    [COMPUTE_RESOURCE_STATUS.CONFIGURE_NETWORK]: () => <RoundedExclamationFilled color={COLORS.WARNING} />,
    [COMPUTE_RESOURCE_STATUS.COMMISSIONING]: () => <RoundedArrowDown color={COLORS.WARNING} />,
};

export const Status: React.FC<IStatusProps> = ({
    status,
    onDetailsClick,
    withoutButton,
}) => {
    const isCompact = useIsDown(BREAKPOINTS.LG);

    if (!statusTranslationMap.hasOwnProperty(status)) {
        return null;
    }

    const Icon = STATUS_ICONS[status];

    const handleDetailsClick = (event: React.SyntheticEvent) => {
        event.preventDefault();

        onDetailsClick && onDetailsClick();
    };

    return (
        <StatusContainer data-cy={COMPUTE_RESOURCE.STATUS} $withoutButton={withoutButton}>
            <Tooltip shown={isCompact} title={statusTranslationMap[status]}>
                <IconContainer>
                    <Icon />
                </IconContainer>
            </Tooltip>

            <StatusLabelContainer>
                <div>{statusTranslationMap[status]}</div>

                {!withoutButton && statusesWithDetails.includes(status) && (
                    <Link onClick={handleDetailsClick}>
                        {
                            status === COMPUTE_RESOURCE_STATUS.CONFIGURE_NETWORK
                                ? <Translate content="computeResource.status.configure" />
                                : <Translate content="computeResource.status.details" />
                        }
                    </Link>
                )}
            </StatusLabelContainer>
        </StatusContainer>
    );
};
